import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

import {
  ALLOWED_ATTACHMENT_FORMAT_LABELS,
  ATTACHMENT_ACCEPT,
  attachmentRejectionWarning,
  formatFileSize,
  MAX_ATTACHMENT_SIZE_BYTES,
  MAX_ATTACHMENTS_PER_RESULT,
  validateAttachment,
} from '../../core/functions/attachment-constraints';
import { TestCaseResult } from '../../models/test-case-result';
import {
  TestCaseResultInsertRequest,
  TestSeverity,
} from '../../models/test-case-result-insert-request';
import { AttachmentService } from '../../services/attachment/attachment.service';
import { TestCaseResultService } from '../../services/test-case-result/test-case-result.service';
import { AttachmentListComponent } from '../../ui/attachment-list/attachment-list.component';

/** A file picked in this drawer, and how far its upload has got. */
export interface PendingAttachment {
  /** Local key; the API's guid only exists once the upload lands. */
  id: number;
  fileName: string;
  sizeBytes: number;
  /** Object URL for an image thumbnail, else null. Revoked when the entry goes. */
  previewUrl: string | null;
  status: 'uploading' | 'uploaded' | 'error';
  percent: number;
  attachmentGuid: string | null;
  error: string | null;
}

@Component({
  selector: 'app-fail-test-case-dialog',
  imports: [MatDialogModule, ReactiveFormsModule, AttachmentListComponent],
  templateUrl: './fail-test-case-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './fail-test-case-dialog.component.scss',
  // AC8: a file dropped anywhere on the drawer counts, not just on the grid. The
  // listeners sit on the host so the whole surface is the target; the grid is what
  // lights up.
  host: {
    '(dragenter)': 'onDragEnter($event)',
    '(dragover)': 'onDragOver($event)',
    '(dragleave)': 'onDragLeave()',
    '(drop)': 'onDrop($event)',
  },
})
export class FailTestCaseDialogComponent {
  readonly data = inject(MAT_DIALOG_DATA);
  private testCaseResultService = inject(TestCaseResultService);
  private attachmentService = inject(AttachmentService);
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  readonly dialogRef = inject(MatDialogRef<FailTestCaseDialogComponent>);

  saving = signal(false);
  serverError = signal<string | null>(null);

  /** Files picked here, in the order chosen. Each uploads on its own the moment it's
      picked, so the tester writes the description while the bytes are already moving. */
  readonly attachments = signal<PendingAttachment[]>([]);
  /** Why the last pick was refused outright (over the file count, mostly). */
  readonly pickError = signal<string | null>(null);
  /** AC7 warnings for files turned away on type or size. They never become thumbnails,
      so this is the only place they are reported. Cleared on the next pick. */
  readonly rejections = signal<string[]>([]);
  /** A file is being dragged over the drawer; drives the grid highlight only. */
  readonly isDraggingOver = signal(false);
  /** Set once the failure is saved and the API handed back files worth showing. */
  readonly savedResult = signal<TestCaseResult | null>(null);

  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('attachmentInput');

  // Ordered low → high; Showstopper is the release-blocking severity (PBI 55097).
  readonly severities: TestSeverity[] = ['Cosmetic', 'Functional', 'Blocker', 'Showstopper'];

  // The drawer's limits, shown in the sub-line under the grid so the rules are visible
  // before a file is chosen rather than after a rejected upload.
  readonly maxAttachments = MAX_ATTACHMENTS_PER_RESULT;
  readonly maxSizeLabel = formatFileSize(MAX_ATTACHMENT_SIZE_BYTES);
  readonly allowedFormats = ALLOWED_ATTACHMENT_FORMAT_LABELS.join(', ');
  readonly accept = ATTACHMENT_ACCEPT;
  readonly formatSize = formatFileSize;

  readonly isUploading = computed(() => this.attachments().some((a) => a.status === 'uploading'));
  /** Files whose upload failed. AC14: the failure can't be filed while any are pending —
      that would drop the tester's evidence without them having said so. */
  readonly failedUploads = computed(() => this.attachments().filter((a) => a.status === 'error'));

  readonly canAddMore = computed(() => this.attachments().length < MAX_ATTACHMENTS_PER_RESULT);
  /** Guids to submit with the result; a file still uploading or rejected has none. */
  readonly uploadedIds = computed(() =>
    this.attachments()
      .map((a) => a.attachmentGuid)
      .filter((guid): guid is string => guid !== null),
  );

  form = this.fb.group({
    comment: new FormControl<string>('', Validators.required),
    patient: new FormControl<string>(''),
    severity: new FormControl<TestSeverity | null>(null, Validators.required),
  });

  private nextId = 1;
  /** Depth of the drag over the drawer; see onDragEnter. */
  private dragDepth = 0;
  /** Per-file, so removing one file aborts only that request. */
  private readonly uploads = new Map<number, Subscription>();
  /** In-flight deletes of removed files, held only so they can be dropped on destroy. */
  private readonly deletes = new Subscription();

  constructor() {
    // Material's own Escape and backdrop handling closes with no result, which the caller
    // reads as "cancelled" — so dismissing the confirmation view that way lost a failure
    // that had already saved: the case stayed unmarked and the stepper didn't advance.
    // Take both paths over and route them through close(), which reports what happened.
    this.dialogRef.disableClose = true;
    this.dialogRef
      .keydownEvents()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.key === 'Escape') this.close();
      });
    this.dialogRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.close());

    // The drawer can close mid-upload with previews on screen; nothing else cleans up.
    this.destroyRef.onDestroy(() => {
      this.uploads.forEach((sub) => sub.unsubscribe());
      this.uploads.clear();
      this.deletes.unsubscribe();
      this.attachments().forEach((a) => this.revokePreview(a));
    });
  }

  selectSeverity(severity: TestSeverity) {
    this.form.controls.severity.setValue(severity);
  }

  /**
   * States in which save() won't submit, so submit carries the disabled attribute for all
   * of them. It used to be disabled for saving/uploading only, which left a click on an
   * incomplete form hitting a button that looked live and did nothing visible.
   *
   * Not a computed(): form.invalid is not a signal, so a computed would latch its first
   * value and never see the form filled in. A method re-runs each change detection pass.
   */
  submitBlocked(): boolean {
    return (
      this.saving() || this.isUploading() || this.failedUploads().length > 0 || this.form.invalid
    );
  }

  /**
   * Why submit is disabled, shown beside it. A disabled button can't be clicked, so it
   * can't explain itself the way save()'s guards do — without this the tester is left
   * with a dimmed button and no idea what it wants.
   */
  submitHint(): string | null {
    // The button's own label already reads "Submitting…" / "Uploading…" here.
    if (this.saving()) return null;
    if (this.isUploading()) return 'Waiting for your files to finish uploading.';

    // AC14: name them. Removing one is the tester's way of saying to file without it.
    const failed = this.failedUploads();
    if (failed.length) {
      return `${failed.map((a) => a.fileName).join(', ')} didn't upload. Remove ${
        failed.length === 1 ? 'it' : 'them'
      } to file this failure without it, or try again.`;
    }

    const missing: string[] = [];
    if (this.form.controls.comment.invalid) missing.push('what happened');
    if (this.form.controls.severity.invalid) missing.push('a severity');
    return missing.length ? `Add ${missing.join(' and ')} to submit.` : null;
  }

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const picked = Array.from(input.files ?? []);
    this.resetInput();
    this.addFiles(picked);
  }

  /**
   * Whether a dropped file could still be attached to this failure. Once the result is
   * being saved, or has saved, it can't: the attachmentIds went with the POST, so a file
   * accepted after that would upload to storage, never be submitted, and never appear —
   * the confirmation view has no grid to show it in.
   */
  acceptsDrops(): boolean {
    return !this.saving() && this.savedResult() === null;
  }

  /** Entering any descendant fires this again, so the nesting is counted rather than
      toggled — otherwise crossing from the drawer onto a field inside it drops the
      highlight mid-drag. */
  onDragEnter(event: DragEvent) {
    if (!this.acceptsDrops() || !this.isFileDrag(event)) return;
    this.dragDepth++;
    this.isDraggingOver.set(true);
  }

  /** Required for the drawer to be a valid drop target at all: without preventDefault
      the browser navigates to the dropped file and the half-written failure is lost. */
  onDragOver(event: DragEvent) {
    if (!this.acceptsDrops() || !this.isFileDrag(event)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    this.isDraggingOver.set(true);
  }

  onDragLeave() {
    this.dragDepth = Math.max(this.dragDepth - 1, 0);
    if (this.dragDepth === 0) this.isDraggingOver.set(false);
  }

  /**
   * preventDefault runs even for a drop this drawer won't accept: the browser's default
   * for a dropped file is to navigate to it, which would throw away a half-written
   * failure. So a drop while saving, or after saving, is swallowed rather than acted on.
   */
  onDrop(event: DragEvent) {
    event.preventDefault();
    this.dragDepth = 0;
    this.isDraggingOver.set(false);
    if (!this.acceptsDrops()) return;

    this.addFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  /** Drop one file: revokes its preview, aborts its upload if it's still in flight, and
      deletes it server-side if it already landed. */
  removeAttachment(id: number) {
    const entry = this.attachments().find((a) => a.id === id);
    if (!entry) return;

    this.uploads.get(id)?.unsubscribe();
    this.uploads.delete(id);
    this.revokePreview(entry);
    this.attachments.update((list) => list.filter((a) => a.id !== id));
    this.pickError.set(null);
    this.rejections.set([]);

    if (entry.attachmentGuid) this.deleteUploaded(entry);
  }

  cancel() {
    this.dialogRef.close({ saved: false });
  }

  /** Header close and the post-save Done button: a saved failure stays saved. */
  close() {
    this.dialogRef.close({ saved: this.savedResult() !== null });
  }

  save() {
    if (this.saving() || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    // Submitting now would silently drop whatever is still in flight.
    if (this.isUploading()) {
      this.pickError.set('Hang on — your files are still uploading.');
      return;
    }

    // AC14: and would drop a file whose upload failed, without the tester ever saying
    // the failure should go in without it. Submit is disabled for this too; this guard
    // covers the Enter key, which submits the form without going through the button.
    const failed = this.failedUploads();
    if (failed.length) {
      this.pickError.set(
        `${failed.map((a) => a.fileName).join(', ')} didn't upload. Remove ` +
          `${failed.length === 1 ? 'it' : 'them'} to file this failure without it.`,
      );
      return;
    }

    this.saving.set(true);
    this.serverError.set(null);

    const attachmentIds = this.uploadedIds();
    const testCaseResultInsert: TestCaseResultInsertRequest = {
      testCaseId: this.data.testCaseId,
      userId: this.data.userId,
      isPass: false,
      comment: this.buildComment(),
      severity: this.form.value.severity!,
      ...(attachmentIds.length ? { attachmentIds } : {}),
    };

    this.testCaseResultService.saveTestCaseResult(testCaseResultInsert).subscribe({
      next: (result) => {
        this.saving.set(false);
        // Show the stored files back with the links the API issued, so the tester can
        // check what actually landed. Nothing attached: close as before.
        if (result?.attachments?.length) this.savedResult.set(result);
        else this.dialogRef.close({ saved: true });
      },
      error: (err) => {
        console.error('Error saving test case failure:', err);
        this.serverError.set('An error occurred while saving. Please try again.');
        this.saving.set(false);
      },
    });
  }

  /** Shared by the picker and a drop: turn away what fails validation, trim the rest to
      allowance, say what was turned away, then start each accepted file uploading. */
  private addFiles(picked: File[]) {
    this.pickError.set(null);
    this.rejections.set([]);
    if (!picked.length) return;

    // AC7: a file that fails validation never reaches the grid, so it isn't counted
    // against the allowance either — only the ones that can actually be uploaded are.
    const rejected = picked.filter((file) => validateAttachment(file) !== null);
    if (rejected.length) {
      this.rejections.set(rejected.map((file) => attachmentRejectionWarning(file.name)));
    }
    const valid = picked.filter((file) => validateAttachment(file) === null);

    const room = Math.max(MAX_ATTACHMENTS_PER_RESULT - this.attachments().length, 0);
    const accepted = valid.slice(0, room);
    const turnedAway = valid.length - accepted.length;
    if (turnedAway > 0) {
      this.pickError.set(
        `A result takes at most ${MAX_ATTACHMENTS_PER_RESULT} files, so ${turnedAway} ` +
          `${turnedAway === 1 ? 'file was' : 'files were'} left out.`,
      );
    }

    accepted.forEach((file) => this.addFile(file));
  }

  /**
   * Release a file that already reached storage. Fire-and-forget on purpose: the entry is
   * gone from the drawer either way, and a failure here leaves an unsubmitted upload the
   * API sweeps up on its own — so it's logged rather than put in front of the tester, who
   * has nothing to do about it.
   */
  private deleteUploaded(entry: PendingAttachment) {
    // add() drops each child again as it finishes, so this doesn't accumulate.
    this.deletes.add(
      this.attachmentService.deleteAttachment(this.data.userId, entry.attachmentGuid!).subscribe({
        error: (err) => console.error(`Failed to delete ${entry.fileName} after removing it:`, err),
      }),
    );
  }

  /** Files being dragged, rather than selected text or a link — those shouldn't light the
      grid up. types is absent on a synthesised event, which counts as a file drag. */
  private isFileDrag(event: DragEvent): boolean {
    const types = event.dataTransfer?.types;
    return !types || Array.from(types).includes('Files');
  }

  /** Only ever called with a file that passed validateAttachment (see addFiles). */
  private addFile(file: File) {
    const id = this.nextId++;

    this.attachments.update((list) => [
      ...list,
      {
        id,
        fileName: file.name,
        sizeBytes: file.size,
        previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        status: 'uploading',
        percent: 0,
        attachmentGuid: null,
        error: null,
      },
    ]);

    const sub = this.attachmentService.uploadAttachment(this.data.userId, file).subscribe({
      next: (event) => {
        if (event.type === 'progress') {
          this.patch(id, { percent: event.percent });
        } else {
          this.patch(id, {
            status: 'uploaded',
            percent: 100,
            attachmentGuid: event.attachment.attachmentGuid,
          });
        }
      },
      error: (err) => {
        console.error(`Failed to upload ${file.name}:`, err);
        this.patch(id, { status: 'error', error: 'Upload failed. Remove it and try again.' });
        this.uploads.delete(id);
      },
      complete: () => this.uploads.delete(id),
    });

    // A synchronous upload (mock mode) has already completed and removed itself.
    if (!sub.closed) this.uploads.set(id, sub);
  }

  private patch(id: number, changes: Partial<PendingAttachment>) {
    this.attachments.update((list) => list.map((a) => (a.id === id ? { ...a, ...changes } : a)));
  }

  private revokePreview(attachment: PendingAttachment) {
    if (attachment.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
  }

  /** Clearing the input's value matters: without it, re-picking the same file fires no
      `change` event and the file never comes back. */
  private resetInput() {
    const input = this.fileInput();
    if (input) input.nativeElement.value = '';
  }

  /** The API stores a single comment + severity, so fold the optional patient detail into
      the comment body. Files go up as real attachments — nothing about them belongs in
      here any more. */
  private buildComment(): string {
    const parts = [this.form.value.comment!.trim()];
    const patient = this.form.value.patient?.trim();
    if (patient) parts.push(`Affected patient: ${patient}`);
    return parts.join('\n\n');
  }
}
